import { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { NOOR } from "@/constants/testIds/noor";
import { BOTTLE_FRONT } from "@/components/noor/bottle";
import { BOTTLES_HIDDEN } from "@/lib/visibility";
import StorySection from "@/components/noor/StorySection";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Launch target — env-driven. Defaults to Jan 1, 2027 in CST (Jalisco time).
const LAUNCH_DATE = process.env.REACT_APP_LAUNCH_DATE || "2027-01-01T00:00:00-06:00";
const LAUNCH_TS = new Date(LAUNCH_DATE).getTime();

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
};
const EASE = [0.22, 1, 0.36, 1];

// Hoisted transition + initial/animate objects — used across hero, editorial,
// countdown, form blocks. Module-scope so React sees stable identities.
const TRANS_WORDMARK   = { duration: 1,   ease: EASE };
const TRANS_HEADLINE   = { duration: 1,   delay: 0.2, ease: EASE };
const TRANS_BODY       = { duration: 1,   delay: 0.4, ease: EASE };
const TRANS_FORM       = { duration: 1,   delay: 0.6, ease: EASE };
const TRANS_HERO_BOTTLE = { duration: 1.6, delay: 0.3, ease: EASE };
const HERO_BOTTLE_INIT = { opacity: 0, scale: 0.96, y: 10 };
const HERO_BOTTLE_ANIM = { opacity: 1, scale: 1, y: 0 };

const STYLE_HERO_GLOW = {
  background: "radial-gradient(ellipse 55% 70% at 50% 100%, rgba(201,164,122,0.20), transparent 70%)",
  pointerEvents: "none",
};
const STYLE_HERO_IMG = { maxHeight: "min(68vh, 620px)" };
const STYLE_PAGE_BG = {
  background:
    "radial-gradient(ellipse 65% 65% at 50% 30%, rgba(201,164,122,0.08), transparent 70%), " +
    "radial-gradient(ellipse 40% 40% at 50% 90%, rgba(59,30,59,0.20), transparent 70%), " +
    "var(--noor-obsidian)",
};
const STYLE_EDITORIAL_HEADLINE = {
  color: "var(--noor-bone)",
  fontSize: "clamp(2.4rem, 4.6vw, 4.2rem)",
};
const STYLE_EDITORIAL_BODY = {
  color: "var(--noor-vellum)",
  fontSize: "clamp(1.05rem, 1.5vw, 1.25rem)",
  lineHeight: 1.65,
};
const STYLE_COUNTDOWN_LABEL = {
  color: "var(--noor-vellum)",
  letterSpacing: "0.42em",
  textTransform: "uppercase",
};
const STYLE_REFINEMENT_ACCENT = { color: "var(--accent-rose)" };

// Teaser silhouette layer — a glimpse, not a reveal. The bottle is heavily
// blurred so the form is felt, not read. We lift the opacity + brightness so
// the silhouette actually registers behind the editorial column without ever
// resolving label details or geometry. Screen blend keeps it luminance-additive
// against the obsidian backdrop; the radial mask vignettes the edges so the
// bottle "emerges" from the darkness instead of sitting on top of it.
const STYLE_TEASER_LAYER = {
  backgroundImage: "url('/noor/bottle-teaser-blur.webp')",
  backgroundRepeat: "no-repeat",
  backgroundPosition: "center 8%",
  backgroundSize: "auto 92%",
  filter: "blur(3px) saturate(125%) brightness(128%) contrast(98%)",
  opacity: 0.72,
  mixBlendMode: "screen",
  WebkitMaskImage:
    "radial-gradient(ellipse 62% 78% at 50% 42%, rgba(0,0,0,1) 0%, rgba(0,0,0,0.92) 50%, rgba(0,0,0,0.5) 78%, transparent 95%)",
  maskImage:
    "radial-gradient(ellipse 62% 78% at 50% 42%, rgba(0,0,0,1) 0%, rgba(0,0,0,0.92) 50%, rgba(0,0,0,0.5) 78%, transparent 95%)",
};

// Hoisted style objects — prevents new object identity on every render.
const STYLE_DIGIT = {
  color: "var(--noor-bone)",
  fontSize: "clamp(2.6rem, 6vw, 5.2rem)",
  letterSpacing: "-0.03em",
};
const STYLE_DIGIT_LABEL = {
  color: "var(--noor-vellum)",
  letterSpacing: "0.36em",
  textTransform: "uppercase",
  fontSize: "clamp(0.55rem, 0.8vw, 0.66rem)",
};
const STYLE_SEPARATOR = {
  color: "var(--noor-rosegold)",
  opacity: 0.5,
  fontSize: "clamp(2.6rem, 6vw, 5.2rem)",
  marginTop: "-0.2em",
};
const STYLE_META_LABEL = {
  color: "var(--noor-vellum)",
  letterSpacing: "0.38em",
  textTransform: "uppercase",
};
const STYLE_META_LABEL_ROSE = {
  color: "var(--accent-rose)",
  letterSpacing: "0.38em",
  textTransform: "uppercase",
};
const STYLE_FOOTER_TEXT = {
  color: "var(--noor-vellum)",
  opacity: 0.6,
  letterSpacing: "0.32em",
  textTransform: "uppercase",
};
const DIGIT_INIT = { opacity: 0.4, y: 6 };
const DIGIT_ANIM = { opacity: 1, y: 0 };
const DIGIT_TRANS = { duration: 0.45, ease: EASE };

function pad(n) { return String(n).padStart(2, "0"); }

function diffParts(ms) {
  const total = Math.max(0, ms);
  const days = Math.floor(total / 86_400_000);
  const hours = Math.floor((total % 86_400_000) / 3_600_000);
  const minutes = Math.floor((total % 3_600_000) / 60_000);
  const seconds = Math.floor((total % 60_000) / 1000);
  return { days, hours, minutes, seconds };
}

function useCountdown(targetTs) {
  const [remaining, setRemaining] = useState(() => targetTs - Date.now());
  useEffect(() => {
    const id = setInterval(() => setRemaining(targetTs - Date.now()), 1000);
    return () => clearInterval(id);
  }, [targetTs]);
  return diffParts(remaining);
}

const STYLE_TAGLINE_SIGNATURE = {
  color: "var(--noor-vellum)",
  fontSize: "clamp(0.95rem, 1.25vw, 1.1rem)",
  letterSpacing: "0.01em",
  lineHeight: 1.5,
};

// --- Story section -----------------------------------------------------------
// Now sourced from the shared `<StorySection />` component so Hero + Manifesto
// render the same poem with the same animation. See StorySection.jsx.
const STYLE_TAGLINE_LIGHT_ACCENT = { color: "var(--noor-rosegold)" };

function MarkN() {
  // Compact NOOR wordmark + filigree underline + brand signature tagline.
  return (
    <div className="flex flex-col items-center gap-3">
      <p
        className="font-display"
        style={{
          color: "var(--noor-bone)",
          fontSize: "clamp(2.2rem, 4.4vw, 3.4rem)",
          letterSpacing: "0.22em",
          fontWeight: 500,
        }}
      >
        NOOR
      </p>
      <span
        className="block h-px"
        style={{ width: 64, background: "var(--noor-rosegold)", opacity: 0.7 }}
      />
      <p
        className="font-mono text-[9px]"
        style={{ color: "var(--noor-vellum)", letterSpacing: "0.42em", textTransform: "uppercase" }}
      >
        Tequila Blanco · Edition Nº 001
      </p>
      <p
        data-testid="coming-soon-tagline"
        className="font-display italic mt-3 text-center"
        style={STYLE_TAGLINE_SIGNATURE}
      >
        Born from <span style={STYLE_TAGLINE_LIGHT_ACCENT}>Light</span>. Crafted for Legends.
      </p>
    </div>
  );
}

function CountdownCell({ value, label, testId }) {
  return (
    <div className="flex flex-col items-center" data-testid={testId}>
      <motion.span
        key={value}
        initial={DIGIT_INIT}
        animate={DIGIT_ANIM}
        transition={DIGIT_TRANS}
        className="font-display tabular-nums leading-none"
        style={STYLE_DIGIT}
      >
        {value}
      </motion.span>
      <span className="font-mono mt-3" style={STYLE_DIGIT_LABEL}>
        {label}
      </span>
    </div>
  );
}

function Separator() {
  return (
    <span aria-hidden className="font-display select-none leading-none" style={STYLE_SEPARATOR}>
      ·
    </span>
  );
}

function Countdown() {
  const { days, hours, minutes, seconds } = useCountdown(LAUNCH_TS);
  const launched = days === 0 && hours === 0 && minutes === 0 && seconds === 0;

  if (launched) {
    return (
      <p
        data-testid="coming-soon-launched"
        className="font-display italic"
        style={{ color: "var(--accent-rose)", fontSize: "clamp(1.8rem, 4vw, 3rem)" }}
      >
        The House is open.
      </p>
    );
  }

  return (
    <div
      data-testid="coming-soon-countdown"
      className="flex items-end justify-center gap-4 sm:gap-6 md:gap-10"
    >
      <CountdownCell value={String(days).padStart(3, "0")} label="Days" testId="countdown-days" />
      <Separator />
      <CountdownCell value={pad(hours)} label="Hours" testId="countdown-hours" />
      <Separator />
      <CountdownCell value={pad(minutes)} label="Minutes" testId="countdown-minutes" />
      <Separator />
      <CountdownCell value={pad(seconds)} label="Seconds" testId="countdown-seconds" />
    </div>
  );
}

function submitButtonLabel(status) {
  if (status === "loading") return "…";
  if (status === "success") return "Welcomed";
  return "Join the list";
}

// Hoisted style objects — referential stability for the form sub-components.
const WAITLIST_OVERLINE_STYLE = {
  color: "var(--accent-rose)",
  letterSpacing: "0.38em",
  textTransform: "uppercase",
};
const WAITLIST_BODY_STYLE = { color: "var(--noor-vellum)", lineHeight: 1.6 };
const WAITLIST_MESSAGE_BASE = { letterSpacing: "0.22em", textTransform: "uppercase" };

function WaitlistHeader() {
  return (
    <>
      <p className="font-mono text-[10px] mb-4" style={WAITLIST_OVERLINE_STYLE}>
        Exclusive Mailing List
      </p>
      <p className="font-display-light text-sm md:text-base mb-6" style={WAITLIST_BODY_STYLE}>
        Join our exclusive mailing list below to be the first to receive updates on our official launch and private release events.
      </p>
    </>
  );
}

function WaitlistInputRow({ email, setEmail, status }) {
  return (
    <div className="flex flex-col sm:flex-row items-stretch gap-3">
      <input
        data-testid="coming-soon-email-input"
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="your.address@example.com"
        disabled={status === "loading"}
        className="input-line flex-1 font-mono text-sm"
        autoComplete="email"
      />
      <button
        data-testid="coming-soon-submit"
        type="submit"
        disabled={status === "loading" || status === "success"}
        className="btn-solid whitespace-nowrap"
      >
        {submitButtonLabel(status)}
      </button>
    </div>
  );
}

function WaitlistMessage({ status, message }) {
  if (!message) return null;
  return (
    <p
      data-testid={`coming-soon-${status}`}
      className="mt-5 font-mono text-[10px]"
      style={{
        ...WAITLIST_MESSAGE_BASE,
        color: status === "success" ? "var(--accent-rose)" : "#E27676",
      }}
    >
      {message}
    </p>
  );
}

function useWaitlistSubmit() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [message, setMessage] = useState(null);

  const submit = useCallback(async (e) => {
    e.preventDefault();
    const value = email.trim();
    if (!value) return;
    setStatus("loading");
    setMessage(null);
    try {
      await axios.post(`${API}/reserve`, { email: value });
      setStatus("success");
      setMessage("You are on the list. We write before we open.");
      setEmail("");
    } catch (err) {
      setStatus("error");
      setMessage(err?.response?.data?.detail || "Could not save the address. Try again in a moment.");
    }
  }, [email]);

  return { email, setEmail, status, message, submit };
}

function WaitlistForm() {
  const { email, setEmail, status, message, submit } = useWaitlistSubmit();
  return (
    <form
      onSubmit={submit}
      data-testid="coming-soon-waitlist"
      className="w-full max-w-lg mx-auto liquid-glass p-6 md:p-7"
    >
      <WaitlistHeader />
      <WaitlistInputRow email={email} setEmail={setEmail} status={status} />
      <WaitlistMessage status={status} message={message} />
    </form>
  );
}

function HeroBottle() {
  return (
    <motion.div
      initial={HERO_BOTTLE_INIT}
      animate={HERO_BOTTLE_ANIM}
      transition={TRANS_HERO_BOTTLE}
      className="relative w-full flex items-center justify-center"
      data-testid="coming-soon-hero-bottle"
    >
      <div className="absolute inset-x-0 bottom-0 h-[60%]" aria-hidden style={STYLE_HERO_GLOW} />
      <img
        src={BOTTLE_FRONT}
        alt="NOOR Tequila Blanco — Edition Nº 001"
        loading="eager"
        decoding="async"
        fetchPriority="high"
        className="w-auto object-contain drop-shadow-[0_40px_120px_rgba(201,164,122,0.25)]"
        style={STYLE_HERO_IMG}
      />
    </motion.div>
  );
}

function Editorial() {
  return (
    <div className="text-center max-w-[58ch] mx-auto" data-testid="coming-soon-editorial">
      <motion.h1
        {...fadeUp}
        transition={TRANS_HEADLINE}
        className="font-display leading-[1.05] tracking-[-0.025em]"
        style={STYLE_EDITORIAL_HEADLINE}
      >
        A new era of{" "}
        <em className="italic" style={STYLE_REFINEMENT_ACCENT}>refinement</em>.
      </motion.h1>
      <motion.p
        {...fadeUp}
        transition={TRANS_BODY}
        className="font-display-light mt-8"
        style={STYLE_EDITORIAL_BODY}
      >
        The wait is almost over. NOOR is arriving soon —
        a vision of elegance, meticulously crafted for those who appreciate the finer things.
      </motion.p>
    </div>
  );
}

function MetaRow() {
  // Where + when, treated like a programme card.
  const launchYear = new Date(LAUNCH_TS).getFullYear();
  return (
    <div
      className="flex flex-wrap items-center justify-center gap-x-10 gap-y-3"
      data-testid="coming-soon-meta"
    >
      <p className="font-mono text-[10px]" style={{ color: "var(--noor-vellum)", letterSpacing: "0.38em", textTransform: "uppercase" }}>
        Cellared in Jalisco
      </p>
      <span className="hidden sm:inline" style={{ color: "var(--border-subtle)" }}>·</span>
      <p className="font-mono text-[10px]" style={{ color: "var(--noor-vellum)", letterSpacing: "0.38em", textTransform: "uppercase" }}>
        Edition Nº 001
      </p>
      <span className="hidden sm:inline" style={{ color: "var(--border-subtle)" }}>·</span>
      <p className="font-mono text-[10px]" style={{ color: "var(--accent-rose)", letterSpacing: "0.38em", textTransform: "uppercase" }}>
        Opening {launchYear}
      </p>
    </div>
  );
}

function MiniFoot() {
  return (
    <footer
      className="absolute bottom-0 left-0 right-0 z-10 pb-6 px-6"
      data-testid="coming-soon-footer"
    >
      <div className="max-w-[1480px] mx-auto flex items-center justify-between flex-wrap gap-3">
        <p className="font-mono text-[9px]" style={{ color: "var(--noor-vellum)", opacity: 0.6, letterSpacing: "0.32em", textTransform: "uppercase" }}>
          © NOOR. Drink responsibly.
        </p>
        <p className="font-mono text-[9px]" style={{ color: "var(--noor-vellum)", opacity: 0.6, letterSpacing: "0.32em", textTransform: "uppercase" }}>
          hello@noor.house
        </p>
      </div>
    </footer>
  );
}

// Scrim that lifts editorial copy off the bottle glimpse. Sits ABOVE the
// glimpse layer but BELOW the content. A vertical band of low-opacity obsidian
// masked horizontally so the bottle silhouette stays visible at the edges
// while the centre column gets a calm reading surface.
const STYLE_TEXT_SCRIM = {
  background:
    "linear-gradient(to bottom, rgba(11,9,15,0.0) 0%, rgba(11,9,15,0.45) 18%, rgba(11,9,15,0.58) 50%, rgba(11,9,15,0.45) 82%, rgba(11,9,15,0.0) 100%)",
  WebkitMaskImage:
    "linear-gradient(to right, transparent 0%, rgba(0,0,0,0.85) 22%, rgba(0,0,0,1) 50%, rgba(0,0,0,0.85) 78%, transparent 100%)",
  maskImage:
    "linear-gradient(to right, transparent 0%, rgba(0,0,0,0.85) 22%, rgba(0,0,0,1) 50%, rgba(0,0,0,0.85) 78%, transparent 100%)",
  pointerEvents: "none",
};

export default function ComingSoon() {
  const launchYear = useMemo(() => new Date(LAUNCH_TS).getFullYear(), []);

  return (
    <main
      data-testid={NOOR.comingSoonPage || "coming-soon-page"}
      className="relative min-h-screen overflow-hidden"
      style={STYLE_PAGE_BG}
    >
      {/* Glimpse layer — heavily blurred bottle silhouette suggesting the form
          beneath the editorial content. Sits between the page background and
          the grain overlay so it reads as part of the atmosphere, not the
          composition. Independent of BOTTLES_HIDDEN — that flag controls the
          full-detail reveal; the silhouette is intentionally a glimpse only. */}
      <div
        aria-hidden
        data-testid="coming-soon-bottle-glimpse"
        className="absolute inset-0 pointer-events-none"
        style={STYLE_TEASER_LAYER}
      />

      {/* Text scrim — vertical reading band that calms the centre column so
          the copy stays crisp without dampening the glimpse at the edges. */}
      <div aria-hidden className="absolute inset-0" style={STYLE_TEXT_SCRIM} />

      <div className="grain-overlay" />

      <div className="relative z-[1] max-w-[1480px] mx-auto px-6 md:px-10 pt-12 md:pt-16 pb-28">
        {/* Wordmark / mark */}
        <motion.div
          {...fadeUp}
          transition={TRANS_WORDMARK}
          className="flex justify-center mb-10 md:mb-12"
        >
          <MarkN />
        </motion.div>

        {/* Two-column hero on lg+: bottle on left, countdown + form on right.
            Mobile: stacks bottle → countdown → editorial → form. When
            BOTTLES_HIDDEN, the bottle column is omitted and the right column
            becomes the only column. */}
        <div className="grid grid-cols-12 gap-y-12 lg:gap-12 items-center mb-12">
          {!BOTTLES_HIDDEN && (
            <div className="col-span-12 lg:col-span-5">
              <HeroBottle />
            </div>
          )}

          <div className={`col-span-12 ${BOTTLES_HIDDEN ? "" : "lg:col-span-7"} flex flex-col gap-10 lg:gap-12`}>
            <motion.div {...fadeUp} transition={TRANS_BODY}>
              <Countdown />
              <p
                className="text-center mt-5 font-mono text-[10px]"
                style={STYLE_COUNTDOWN_LABEL}
              >
                Until the first pour · {launchYear}
              </p>
            </motion.div>

            <Editorial />

            <StorySection testId="coming-soon-story" />

            <motion.div {...fadeUp} transition={TRANS_FORM}>
              <WaitlistForm />
            </motion.div>
          </div>
        </div>

        <MetaRow />
      </div>

      <MiniFoot />
    </main>
  );
}
